/**
 * The JSON Schema subset the driver registry uses (link_schema / address_schema,
 * platform-core/internal/iot/drivers/schemas), turned into form fields, form
 * values and back, and validated with ajv (draft 2020-12, the registry's).
 *
 * Supported: a flat object schema with `properties` of `type` string / integer /
 * number / boolean or an `enum`; `default`, `minimum` / `maximum`, `minLength`,
 * `pattern`, `format: password` + `writeOnly`, `title`, `description`,
 * `examples`, `required`; and the rendering hints `x-ui-order`, `x-ui-show-if`
 * ({field: {other: value | [values]}}), `x-ui-labels` (enum value → label) and
 * `x-ui-suggestions` (values offered, not enforced) and `x-ui-advanced` (true: an
 * optional field the form folds under "Advanced settings"). Anything else in a schema
 * is ignored by the form and left to ajv and the server.
 *
 * Form values are what the inputs hold (strings, and booleans for checkboxes);
 * {@link formToParams} coerces them to the typed params the API takes. Pure
 * logic, no React, so it is unit-tested directly.
 */

import Ajv2020, { type ErrorObject, type ValidateFunction } from "ajv/dist/2020";
import type { JsonSchema } from "@/lib/api/things";

/** What an input holds: text for every field but a boolean checkbox. */
export type FormValue = string | boolean;
export type FormValues = Record<string, FormValue>;

export type FieldKind = "text" | "password" | "integer" | "number" | "boolean" | "enum";

export interface FieldSpec {
  name: string;
  label: string;
  description?: string;
  kind: FieldKind;
  required: boolean;
  /** Enum values, typed as in the schema. */
  enumValues?: Array<string | number | boolean>;
  /** Enum value (as text) → label. */
  enumLabels?: Record<string, string>;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  /** Values offered in a datalist (x-ui-suggestions), not enforced. */
  suggestions?: Array<string | number>;
  /** A placeholder from `examples`. */
  example?: string;
  /** Write-only secret (OPC UA password): never read back. */
  writeOnly: boolean;
  /** The schema marks it `x-ui-advanced`: folded away in the form unless required. */
  advanced?: boolean;
}

type Prop = Record<string, unknown>;

function props(schema: JsonSchema | null | undefined): Record<string, Prop> {
  const p = schema && typeof schema === "object" ? (schema as { properties?: unknown }).properties : undefined;
  return p && typeof p === "object" ? (p as Record<string, Prop>) : {};
}

function requiredOf(schema: JsonSchema | null | undefined): Set<string> {
  const r = schema ? (schema as { required?: unknown }).required : undefined;
  return new Set(Array.isArray(r) ? r.filter((x): x is string => typeof x === "string") : []);
}

/** The field order: x-ui-order first, then any other property in schema order. */
function order(schema: JsonSchema | null | undefined): string[] {
  const names = Object.keys(props(schema));
  const hinted = schema ? (schema as { "x-ui-order"?: unknown })["x-ui-order"] : undefined;
  const first = Array.isArray(hinted) ? hinted.filter((n): n is string => typeof n === "string" && names.includes(n)) : [];
  return [...first, ...names.filter((n) => !first.includes(n))];
}

function kindOf(p: Prop): FieldKind {
  if (Array.isArray(p.enum)) return "enum";
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== "null") : p.type;
  if (t === "integer") return "integer";
  if (t === "number") return "number";
  if (t === "boolean") return "boolean";
  if (p.format === "password" || p.writeOnly === true) return "password";
  return "text";
}

const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/** Every property of an object schema as a form field, in display order. */
export function schemaFields(schema: JsonSchema | null | undefined): FieldSpec[] {
  const all = props(schema);
  const req = requiredOf(schema);
  return order(schema).map((name) => {
    const p = all[name] ?? {};
    const labels = p["x-ui-labels"];
    const suggestions = p["x-ui-suggestions"];
    const examples = p.examples;
    return {
      name,
      label: typeof p.title === "string" && p.title ? p.title : name,
      description: typeof p.description === "string" ? p.description : undefined,
      kind: kindOf(p),
      required: req.has(name),
      enumValues: Array.isArray(p.enum) ? (p.enum as Array<string | number | boolean>) : undefined,
      enumLabels:
        labels && typeof labels === "object" ? Object.fromEntries(Object.entries(labels as Record<string, string>)) : undefined,
      default: p.default,
      minimum: num(p.minimum),
      maximum: num(p.maximum),
      pattern: typeof p.pattern === "string" ? p.pattern : undefined,
      suggestions: Array.isArray(suggestions)
        ? suggestions.filter((s): s is string | number => typeof s === "string" || typeof s === "number")
        : undefined,
      example: Array.isArray(examples) && examples.length > 0 ? String(examples[0]) : undefined,
      writeOnly: p.writeOnly === true || p.format === "password",
      ...(p["x-ui-advanced"] === true ? { advanced: true } : {}),
    };
  });
}

/** A value as form text ("" for none). */
function toFormValue(v: unknown, kind: FieldKind): FormValue {
  if (kind === "boolean") return v === true;
  if (v === undefined || v === null) return "";
  return String(v);
}

/**
 * The value a field has for the show-if check: the form's, else the field's
 * default (a hidden-by-default field depends on what the operator sees).
 */
function effective(values: FormValues, field: FieldSpec | undefined, name: string): unknown {
  const v = values[name];
  if (v !== undefined && v !== "") return v;
  return field?.default;
}

/** Whether a field is shown, per `x-ui-show-if` ({field: {other: value | [values]}}; every condition must hold). */
export function isFieldVisible(schema: JsonSchema | null | undefined, name: string, values: FormValues): boolean {
  const rules = schema ? (schema as { "x-ui-show-if"?: unknown })["x-ui-show-if"] : undefined;
  if (!rules || typeof rules !== "object") return true;
  const cond = (rules as Record<string, unknown>)[name];
  if (!cond || typeof cond !== "object") return true;
  const fields = new Map(schemaFields(schema).map((f) => [f.name, f]));
  return Object.entries(cond as Record<string, unknown>).every(([other, want]) => {
    const have = effective(values, fields.get(other), other);
    const wants = Array.isArray(want) ? want : [want];
    return wants.some((w) => String(w) === String(have));
  });
}

/**
 * The form's starting values: each field's default, overridden by `prefill`
 * (discover-hit data, or an existing link's params). Secrets start blank.
 */
export function formDefaults(schema: JsonSchema | null | undefined, prefill?: Record<string, unknown> | null): FormValues {
  const out: FormValues = {};
  for (const f of schemaFields(schema)) {
    const pre = prefill?.[f.name];
    out[f.name] = f.writeOnly ? "" : toFormValue(pre !== undefined && pre !== null ? pre : f.default, f.kind);
  }
  return out;
}

export interface ToParamsOptions {
  /** Secrets already stored on the instrument (`secrets_set`): a blank one is kept by leaving it out. */
  secretsSet?: readonly string[];
  /** Stored secrets to clear: sent as "" (PATCH semantics). */
  clearSecrets?: ReadonlySet<string>;
}

/**
 * Form values → the params object the API takes: text coerced to integers /
 * numbers / enum values, hidden fields and blank optional fields left out (the
 * gateway applies its defaults), and a blank secret left out so a stored one
 * is kept. A value that does not parse is passed as typed, for ajv and the
 * server to name.
 */
export function formToParams(
  schema: JsonSchema | null | undefined,
  values: FormValues,
  opts: ToParamsOptions = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of schemaFields(schema)) {
    if (!isFieldVisible(schema, f.name, values)) continue;
    const raw = values[f.name];
    if (f.writeOnly) {
      if (opts.clearSecrets?.has(f.name)) out[f.name] = "";
      else if (typeof raw === "string" && raw !== "") out[f.name] = raw;
      continue;
    }
    if (f.kind === "boolean") {
      if (typeof raw === "boolean") out[f.name] = raw;
      continue;
    }
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text === "") continue;
    if (f.kind === "integer" || f.kind === "number") {
      const n = Number(text);
      out[f.name] = Number.isFinite(n) && (f.kind === "number" || Number.isInteger(n)) ? n : text;
      continue;
    }
    if (f.kind === "enum") {
      const match = f.enumValues?.find((e) => String(e) === text);
      out[f.name] = match !== undefined ? match : text;
      continue;
    }
    out[f.name] = text;
  }
  return out;
}

/** An existing link's / address's params → form values (secrets blank). */
export function paramsToForm(schema: JsonSchema | null | undefined, params: Record<string, unknown> | null | undefined): FormValues {
  return formDefaults(schema, params ?? {});
}

// ── Validation ───────────────────────────────────────────────────────────────

// The registry's schemas are draft 2020-12, so ajv's 2020 entry point. Not strict:
// the x-ui-* hints are unknown keywords by design, and `format: password` is a
// rendering hint, not something to validate.
const ajv = new Ajv2020({ strict: false, allErrors: true, validateFormats: false });
const compiled = new WeakMap<object, ValidateFunction | null>();

function validatorFor(schema: JsonSchema): ValidateFunction | null {
  if (compiled.has(schema)) return compiled.get(schema) ?? null;
  let v: ValidateFunction | null = null;
  try {
    v = ajv.compile(schema);
  } catch {
    // A schema this ajv cannot compile is left to the server to check.
    v = null;
  }
  compiled.set(schema, v);
  return v;
}

function messageFor(e: ErrorObject, field: FieldSpec | undefined): string {
  const p = e.params as Record<string, unknown>;
  switch (e.keyword) {
    case "required":
      return "Required.";
    case "minimum":
      return `At least ${String(p.limit)}.`;
    case "maximum":
      return `At most ${String(p.limit)}.`;
    case "type":
      return p.type === "integer" ? "A whole number." : p.type === "number" ? "A number." : `Must be ${String(p.type)}.`;
    case "enum":
      return "Pick one of the listed values.";
    case "minLength":
      return "Required.";
    case "pattern":
      return field?.example ? `Not in the expected form, e.g. ${field.example}.` : "Not in the expected form.";
    case "additionalProperties":
      return `Unknown field ${String(p.additionalProperty)}.`;
    default:
      return e.message ? `${e.message.charAt(0).toUpperCase()}${e.message.slice(1)}.` : "Not valid.";
  }
}

export interface SchemaValidation {
  /** Field name → message; "" for a problem not tied to one field. */
  errors: Record<string, string>;
  valid: boolean;
}

/**
 * Validate typed params (from {@link formToParams}) against the schema. A
 * required secret that is already stored (`secretsSet`) counts as present.
 */
export function validateParams(
  schema: JsonSchema | null | undefined,
  params: Record<string, unknown>,
  opts: { secretsSet?: readonly string[] } = {},
): SchemaValidation {
  if (!schema || typeof schema !== "object") return { errors: {}, valid: true };
  const validate = validatorFor(schema);
  if (!validate) return { errors: {}, valid: true };
  const probe = { ...params };
  for (const s of opts.secretsSet ?? []) if (!(s in probe)) probe[s] = "stored";
  if (validate(probe)) return { errors: {}, valid: true };
  const fields = new Map(schemaFields(schema).map((f) => [f.name, f]));
  const errors: Record<string, string> = {};
  for (const e of validate.errors ?? []) {
    const name =
      e.keyword === "required"
        ? String((e.params as { missingProperty?: unknown }).missingProperty ?? "")
        : e.instancePath.replace(/^\//, "").split("/")[0];
    if (!(name in errors)) errors[name] = messageFor(e, fields.get(name));
  }
  return { errors, valid: false };
}
